// EntityTagsSection — fetches + renders the tags attached to a single
// entity, paired with a TagPicker for attach / detach + inline create.
//
// CHG-1325 / ITER-3452 — Tag taxonomy phase 3 (FE integration).
//
// Used as the "Tags" tab body on the 6 taggable-entity detail pages
// (donor / campaign / recurring_donation / pledge / donation / message)
// via override-block:custom-tabs in each generator slot config.
//
// Owns its react-query state so each detail page can mount it without
// boilerplate. Three queries:
//   - listEntityTags(entityType, entityId) — current tags on this entity
//   - listTags()                            — full org tag inventory (TagPicker)
// Three mutations: attachTag, detachTag, createTag (the last only when
// the user has tag.create + chose the inline-create option).
//
// Failure mode: any backend error surfaces inline as a card with the
// error message + a retry button. The TagPicker stays mounted so the
// user can retry attach/detach without re-fetching.

import { useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { PermissionGate } from "../../auth/PermissionGate";
import {
  attachTag,
  createTag,
  detachTag,
  listEntityTags,
  listTags,
  type TaggableEntityType,
} from "../../api/tags";
import type { Tag } from "../../api/types";
import { TagPicker } from "./TagPicker";

export interface EntityTagsSectionProps {
  entityType: TaggableEntityType;
  entityId: string;
}

const TAG_READ_PERM = "alora-giving.tag.read";

export function EntityTagsSection({
  entityType,
  entityId,
}: EntityTagsSectionProps) {
  const qc = useQueryClient();

  const entityTagsKey = [
    "tags",
    "for-entity",
    entityType,
    entityId,
  ] as const;
  const allTagsKey = ["tags", "list", "all-for-picker"] as const;

  const entityTagsQ = useQuery({
    queryKey: entityTagsKey,
    queryFn: () => listEntityTags(entityType, entityId),
  });

  const allTagsQ = useQuery({
    queryKey: allTagsKey,
    queryFn: () => listTags({ pageSize: 200 }),
  });

  const attachMut = useMutation({
    mutationFn: (tagId: string) => attachTag(entityType, entityId, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: entityTagsKey });
      qc.invalidateQueries({ queryKey: allTagsKey });
    },
  });
  const detachMut = useMutation({
    mutationFn: (tagId: string) => detachTag(entityType, entityId, tagId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: entityTagsKey });
      qc.invalidateQueries({ queryKey: allTagsKey });
    },
  });
  const createAndAttachMut = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const tag = await createTag({ name, color });
      await attachTag(entityType, entityId, tag.id);
      return tag;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: entityTagsKey });
      qc.invalidateQueries({ queryKey: allTagsKey });
    },
  });

  const selectedTagIds = useMemo(
    () => (entityTagsQ.data ?? []).map((et) => et.tag_id),
    [entityTagsQ.data],
  );
  const allTags: Tag[] = useMemo(
    () => allTagsQ.data?.items ?? [],
    [allTagsQ.data],
  );

  const anyError =
    entityTagsQ.error ?? allTagsQ.error ?? attachMut.error ?? detachMut.error ?? createAndAttachMut.error;

  return (
    <PermissionGate
      permission={TAG_READ_PERM}
      fallback={
        <div
          className="card"
          style={{ padding: 16, color: "var(--color-text-muted)" }}
        >
          You don't have permission to view tags.
        </div>
      }
    >
      <div
        className="card"
        style={{
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
        data-testid={`entity-tags-${entityType}`}
      >
        <div style={{ fontSize: 14, fontWeight: 500 }}>Tags</div>

        {anyError ? (
          <div
            role="alert"
            style={{ color: "var(--color-error)", fontSize: 12 }}
          >
            {(anyError as Error).message}
          </div>
        ) : null}

        <TagPicker
          allTags={allTags}
          selectedTagIds={selectedTagIds}
          onAttach={(tagId) => attachMut.mutateAsync(tagId)}
          onDetach={(tagId) => detachMut.mutateAsync(tagId)}
          onCreate={(name, color) =>
            createAndAttachMut.mutateAsync({ name, color })
          }
          disabled={entityTagsQ.isLoading || allTagsQ.isLoading}
          placeholder="Add or create a tag…"
        />
      </div>
    </PermissionGate>
  );
}
